import { severityClass, fmtDate } from "../lib/utils";

function Field({ label, value, mono = true }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="overline">{label}</span>
      <span
        className={`text-sm text-slate-900 ${mono ? "font-mono" : ""} break-words`}
      >
        {value || "-"}
      </span>
    </div>
  );
}

export default function CVEMetadata({ data }) {
  const nvd = data?.nvd || {};
  const cvss = nvd.cvss || {};
  const sev = (cvss.baseSeverity || "").toUpperCase();
  const sevClass = severityClass(sev);

  return (
    <section
      data-testid="cve-metadata"
      className="panel p-6 md:p-7 fade-up"
    >
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
        <div>
          <div className="overline mb-2">CVE Identifier</div>
          <h2 className="font-mono text-2xl md:text-3xl text-slate-900">
            {nvd.id || data.id}
          </h2>
          <p className="text-slate-600 text-sm md:text-base mt-3 max-w-3xl leading-relaxed">
            {nvd.description || data?.advisory?.summary || "-"}
          </p>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <div
            data-testid="cve-severity-badge"
            className={`border ${sevClass} rounded-md px-3 py-2 min-w-[120px]`}
          >
            <div className="overline" style={{ color: "inherit", opacity: 0.7 }}>
              CVSS {cvss.version || ""}
            </div>
            <div className="font-mono text-2xl font-bold leading-none mt-1">
              {cvss.baseScore ?? "-"}
            </div>
            <div className="text-[10px] uppercase tracking-wider font-bold mt-1">
              {sev || "UNRATED"}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4 border-t border-slate-100 pt-5">
        <Field label="Published" value={fmtDate(nvd.published)} />
        <Field label="Last Modified" value={fmtDate(nvd.lastModified)} />
        <Field label="Vuln Status" value={nvd.vulnStatus || "-"} />
        <Field label="Source" value={nvd.sourceIdentifier || "-"} />

        <Field label="Attack Vector" value={cvss.attackVector || "-"} />
        <Field label="Attack Complexity" value={cvss.attackComplexity || "-"} />
        <Field label="Privileges Required" value={cvss.privilegesRequired || "-"} />
        <Field label="User Interaction" value={cvss.userInteraction || "-"} />

        <Field label="Confidentiality" value={cvss.confidentialityImpact || "-"} />
        <Field label="Integrity" value={cvss.integrityImpact || "-"} />
        <Field label="Availability" value={cvss.availabilityImpact || "-"} />
        <Field label="Weaknesses" value={(nvd.weaknesses || []).join(", ") || "-"} />
      </div>

      {cvss.vectorString && (
        <div className="mt-5 pt-5 border-t border-slate-100">
          <div className="overline mb-2">CVSS Vector</div>
          <code className="font-mono text-xs text-violet-700 break-all">
            {cvss.vectorString}
          </code>
        </div>
      )}

      <div className="mt-5 pt-5 border-t border-slate-100 flex flex-wrap gap-4">
        <SourceTag
          label="NVD"
          present={data?.sources?.nvd}
          testid="src-nvd"
        />
        <SourceTag
          label="MITRE"
          present={data?.sources?.mitre}
          testid="src-mitre"
        />
        <SourceTag
          label="GitHub Advisory"
          present={data?.sources?.github_advisory}
          testid="src-gha"
        />
        <SourceTag
          label={`Patch diffs (${data?.sources?.patch_diffs ?? 0})`}
          present={(data?.sources?.patch_diffs ?? 0) > 0}
          testid="src-patches"
        />
      </div>
    </section>
  );
}

function SourceTag({ label, present, testid }) {
  return (
    <div data-testid={testid} className="flex items-center gap-2 text-xs">
      <span
        className={`h-2 w-2 rounded-full ${present ? "bg-violet-600" : "bg-slate-300"}`}
      />
      <span className={present ? "text-slate-700" : "text-slate-500"}>{label}</span>
    </div>
  );
}
